import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, UploadSimple, DownloadSimple, Bank, CheckCircle, Paperclip, Circle, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ArchiveButton } from "@/components/ArchiveButton";
import { Checkbox } from "@/components/ui/checkbox";

const EMPTY = { bank_name: "", account_no: "", statement_month: "", title: "", notes: "", file_name: "", file_url: "", size: 0, content_type: "" };

function monthLabel(m) {
  if (!m || m.length !== 7) return m || "—";
  const [y, mm] = m.split("-");
  return new Date(Number(y), Number(mm) - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
}
function fileHref(url) {
  if (!url) return "#";
  return url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL || ""}${url}`;
}
function humanSize(bytes) {
  if (!bytes) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

export default function BankReconciliationPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [filterMonth, setFilterMonth] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // Match dialog state
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchRow, setMatchRow] = useState(null);
  const [matchInvoices, setMatchInvoices] = useState([]);
  const [matchDisbursements, setMatchDisbursements] = useState([]);
  const [matchInvSelected, setMatchInvSelected] = useState(new Set());
  const [matchDisSelected, setMatchDisSelected] = useState(new Set());
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchSaving, setMatchSaving] = useState(false);
  const [matchTab, setMatchTab] = useState("in");

  async function openMatch(row) {
    setMatchRow(row); setMatchOpen(true); setMatchLoading(true); setMatchTab("in");
    try {
      const { data } = await api.get(`/bank-reconciliations/${row.id}/match-candidates`);
      setMatchInvoices(data.invoices || []);
      setMatchDisbursements(data.disbursements || []);
      setMatchInvSelected(new Set(data.matched_invoice_ids || []));
      setMatchDisSelected(new Set(data.matched_disbursement_ids || []));
    } finally { setMatchLoading(false); }
  }
  function toggleInv(id) { setMatchInvSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); }
  function toggleDis(id) { setMatchDisSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); }

  async function saveMatches() {
    setMatchSaving(true);
    try {
      const { data } = await api.post(`/bank-reconciliations/${matchRow.id}/match`, {
        invoice_ids: Array.from(matchInvSelected),
        disbursement_ids: Array.from(matchDisSelected),
      });
      toast.success(`Linked ${data.matched_invoice_count} inv (₱${(data.matched_invoice_total||0).toLocaleString()}) · ${data.matched_disbursement_count} disb (₱${(data.matched_disbursement_total||0).toLocaleString()})`);
      setMatchOpen(false); setMatchRow(null);
      setMatchInvoices([]); setMatchDisbursements([]); setMatchInvSelected(new Set()); setMatchDisSelected(new Set());
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setMatchSaving(false); }
  }
  const invTotal = matchInvoices.filter((i) => matchInvSelected.has(i.id)).reduce((s, i) => s + (i.amount_paid || i.total_amount || 0), 0);
  const disTotal = matchDisbursements.filter((d) => matchDisSelected.has(d.id)).reduce((s, d) => s + (d.total_amount || 0), 0);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (filterMonth) params.month = filterMonth;
      if (filterStatus === "reconciled") params.reconciled = true;
      if (filterStatus === "unreconciled") params.reconciled = false;
      const { data } = await api.get("/bank-reconciliations", { params });
      setItems(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterMonth, filterStatus]);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/bank_reconciliations`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body,
      });
      if (!res.ok) throw new Error("upload failed");
      const data = await res.json();
      setForm((f) => ({ ...f, file_name: data.file_name, file_url: data.file_url, size: data.size, content_type: data.content_type }));
      toast.success("Statement attached — fill the details and Save");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.bank_name) { toast.error("Bank name is required"); return; }
    if (!form.statement_month) { toast.error("Pick the statement month"); return; }
    if (!form.file_url) { toast.error("Attach the bank statement file"); return; }
    if (!form.title) { toast.error("Give this statement a title"); return; }
    try {
      await api.post("/bank-reconciliations", form);
      toast.success("Statement uploaded");
      setOpen(false); setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  }

  async function toggleReconciled(row) {
    try {
      const { data } = await api.post(`/bank-reconciliations/${row.id}/toggle`);
      toast.success(data.reconciled ? "Marked reconciled" : "Marked unreconciled");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }

  async function handleExportXlsx() {
    try {
      const token = localStorage.getItem("access_token");
      const params = new URLSearchParams();
      if (filterMonth) params.append("month", filterMonth);
      if (filterStatus === "reconciled") params.append("reconciled", "true");
      if (filterStatus === "unreconciled") params.append("reconciled", "false");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/bank-reconciliations/export/xlsx${params.toString() ? "?" + params.toString() : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `bank_reconciliations_${stamp}.xlsx`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success("Bank reconciliations exported");
    } catch { toast.error("Export failed"); }
  }

  return (
    <div className="space-y-6" data-testid="bank-recon-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2 flex items-center gap-1.5"><Bank size={12} /> Bank Reconciliation</div>
          <h1 className="section-title text-3xl sm:text-4xl">Monthly Bank Statements</h1>
          <p className="text-sm text-muted-foreground mt-1">Upload monthly bank statements, keep them tagged by bank + month, and mark each one as reconciled once it's been matched to Receivables and Billing.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-border" onClick={handleExportXlsx} data-testid="export-bank-recons-btn">
            <DownloadSimple size={16} className="mr-2" /> Export Excel
          </Button>
          <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-bank-recon-btn"><Plus size={16} className="mr-2" /> Upload Statement</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Month</Label>
          <Input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="border-border w-[180px]" data-testid="filter-month" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Status</Label>
          <div className="inline-flex border border-border rounded overflow-hidden text-xs">
            {[["all", "All"], ["reconciled", "Reconciled"], ["unreconciled", "Unreconciled"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilterStatus(v)} className={`px-3 py-2 ${filterStatus === v ? "bg-foreground text-background" : "bg-card hover:bg-muted"}`} data-testid={`filter-${v}`}>{l}</button>
            ))}
          </div>
        </div>
        {(filterMonth || filterStatus !== "all") && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterMonth(""); setFilterStatus("all"); }} data-testid="clear-filters">Clear</Button>
        )}
      </div>

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead>
              <TableHead>Bank</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>File</TableHead>
              <TableHead>Uploaded By</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">No bank statements yet. Click Upload Statement to add one.</TableCell></TableRow>
            ) : items.map((r) => (
              <TableRow key={r.id} data-testid={`bank-recon-row-${r.id}`}>
                <TableCell className="font-medium tabular-nums">{monthLabel(r.statement_month)}</TableCell>
                <TableCell>
                  <div>{r.bank_name}</div>
                  {r.account_no && <div className="text-xs text-muted-foreground font-mono">{r.account_no}</div>}
                </TableCell>
                <TableCell>
                  <div>{r.title}</div>
                  {r.notes && <div className="text-xs text-muted-foreground mt-0.5 max-w-md truncate" title={r.notes}>{r.notes}</div>}
                </TableCell>
                <TableCell>
                  <a href={fileHref(r.file_url)} target="_blank" rel="noreferrer" className="text-sm hover:underline flex items-center gap-1.5" data-testid={`download-recon-${r.id}`}>
                    <DownloadSimple size={14} /> {r.file_name || "download"}
                    {r.size ? <span className="text-[10px] text-muted-foreground">({humanSize(r.size)})</span> : null}
                  </a>
                </TableCell>
                <TableCell className="text-sm">{r.uploaded_by_name}</TableCell>
                <TableCell>
                  {r.reconciled ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle size={14} weight="fill" /> Reconciled</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-700"><Circle size={14} /> Unreconciled</span>
                  )}
                  {r.reconciled_at && <div className="text-[10px] text-muted-foreground">{r.reconciled_by_name} · {r.reconciled_at.slice(0, 10)}</div>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2 items-center">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground text-right">
                      {(r.matched_invoice_ids || []).length ? <div>IN: {(r.matched_invoice_ids || []).length} · ₱{(r.matched_total || 0).toLocaleString()}</div> : null}
                      {(r.matched_disbursement_ids || []).length ? <div>OUT: {(r.matched_disbursement_ids || []).length} · ₱{(r.matched_disbursement_total || 0).toLocaleString()}</div> : null}
                    </div>
                    <Button size="sm" variant="outline" className="border-border" onClick={() => openMatch(r)} data-testid={`match-recon-${r.id}`}>
                      <LinkSimple size={14} className="mr-1" /> Match
                    </Button>
                    <Button size="sm" variant="outline" className="border-border" onClick={() => toggleReconciled(r)} data-testid={`toggle-recon-${r.id}`}>
                      {r.reconciled ? "Un-mark" : "Mark reconciled"}
                    </Button>
                    {(user?.role === "admin" || r.uploaded_by === user?.id) && !r.deleted_at && (
                      <ArchiveButton endpoint={`/bank-reconciliations/${r.id}`} label={r.title} onArchived={load} testId={`archive-recon-${r.id}`} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="bank-recon-dialog">
          <DialogHeader><DialogTitle className="font-heading font-black">Upload Bank Statement</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Bank Name</Label>
                <Input required value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="e.g. BDO Unibank" className="border-border" data-testid="recon-bank-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Account No (optional)</Label>
                <Input value={form.account_no} onChange={(e) => setForm({ ...form, account_no: e.target.value })} className="border-border font-mono" data-testid="recon-account-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Statement Month</Label>
                <Input type="month" required value={form.statement_month} onChange={(e) => setForm({ ...form, statement_month: e.target.value })} className="border-border" data-testid="recon-month-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Title</Label>
                <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Feb 2026 BDO SOA" className="border-border" data-testid="recon-title-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-border" data-testid="recon-notes-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">File</Label>
              {form.file_url ? (
                <div className="flex items-center justify-between border border-border rounded px-3 py-2 text-sm bg-muted/30">
                  <span className="flex items-center gap-2 truncate"><Paperclip size={14} /> {form.file_name} <span className="text-[10px] text-muted-foreground">({humanSize(form.size)})</span></span>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, file_name: "", file_url: "", size: 0 })}>Replace</Button>
                </div>
              ) : (
                <label className="border border-dashed border-border rounded p-4 flex flex-col items-center gap-1 cursor-pointer hover:bg-muted/40 transition text-sm" data-testid="recon-file-label">
                  <UploadSimple size={20} className="text-muted-foreground" />
                  <span>{uploading ? "Uploading…" : "Click to select PDF / XLSX / CSV"}</span>
                  <input type="file" onChange={handleFile} className="hidden" disabled={uploading} data-testid="recon-file-input" />
                </label>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={uploading} data-testid="recon-submit-btn">Save statement</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Match transactions dialog */}
      <Dialog open={matchOpen} onOpenChange={setMatchOpen}>
        <DialogContent className="max-w-2xl" data-testid="match-invoices-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black">Link Transactions</DialogTitle>
            <p className="text-xs text-muted-foreground">Pick every paid invoice (money IN) and every approved voucher / OR (money OUT) covered by <b>{matchRow?.title}</b> for <b>{matchRow ? monthLabel(matchRow.statement_month) : ""}</b>.</p>
          </DialogHeader>

          {/* Tabs */}
          <div className="inline-flex border border-border rounded overflow-hidden text-xs w-fit">
            <button type="button" onClick={() => setMatchTab("in")} className={`px-3 py-1.5 ${matchTab === "in" ? "bg-foreground text-background" : "bg-card hover:bg-muted"}`} data-testid="match-tab-in">Receivables IN ({matchInvSelected.size})</button>
            <button type="button" onClick={() => setMatchTab("out")} className={`px-3 py-1.5 border-l border-border ${matchTab === "out" ? "bg-foreground text-background" : "bg-card hover:bg-muted"}`} data-testid="match-tab-out">Disbursements OUT ({matchDisSelected.size})</button>
          </div>

          {matchLoading ? (
            <div className="text-sm text-muted-foreground py-6">Loading transactions…</div>
          ) : matchTab === "in" ? (
            matchInvoices.length === 0 ? (
              <div className="text-sm text-muted-foreground border border-dashed border-border rounded p-6 text-center">No paid invoices found for this month.</div>
            ) : (
              <div className="border border-border rounded max-h-[380px] overflow-y-auto divide-y divide-border">
                {matchInvoices.map((inv) => {
                  const checked = matchInvSelected.has(inv.id);
                  return (
                    <label key={inv.id} className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/40 ${checked ? "bg-muted/60" : ""}`} data-testid={`match-inv-${inv.id}`}>
                      <Checkbox checked={checked} onCheckedChange={() => toggleInv(inv.id)} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-mono text-xs">{inv.invoice_no}</span>
                          <span className="tabular-nums text-sm font-heading font-bold">+₱{(inv.amount_paid || inv.total_amount || 0).toLocaleString()}</span>
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{inv.client_name} · issued {inv.issue_date?.slice(0, 10) || (inv.created_at || "").slice(0, 10)}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )
          ) : (
            matchDisbursements.length === 0 ? (
              <div className="text-sm text-muted-foreground border border-dashed border-border rounded p-6 text-center">No approved (completed) Operational Requests / Cash Vouchers / Check Vouchers found for this month.</div>
            ) : (
              <div className="border border-border rounded max-h-[380px] overflow-y-auto divide-y divide-border">
                {matchDisbursements.map((d) => {
                  const checked = matchDisSelected.has(d.id);
                  const TYPE_SHORT = { operational: "OR", cash_voucher: "CV", check_voucher: "CHV", budget: "BR" };
                  const short = TYPE_SHORT[d.request_type] || (d.request_no || "").split("-")[0] || "REQ";
                  return (
                    <label key={d.id} className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/40 ${checked ? "bg-muted/60" : ""}`} data-testid={`match-dis-${d.id}`}>
                      <Checkbox checked={checked} onCheckedChange={() => toggleDis(d.id)} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-mono text-xs">{d.request_no}</span>
                          <span className="tabular-nums text-sm font-heading font-bold text-rose-700">-₱{(d.total_amount || 0).toLocaleString()}</span>
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          <span className="inline-block border border-border rounded px-1 mr-1 uppercase tracking-widest text-[10px]">{short}</span>
                          {d.title} · {d.department} · {d.request_date || (d.created_at || "").slice(0, 10)}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )
          )}

          <div className="border-t border-border pt-3 space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <div className="text-muted-foreground">Money IN (selected)</div>
              <div className="font-heading font-bold tabular-nums text-emerald-700" data-testid="match-in-total">+₱{invTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-[10px] text-muted-foreground ml-1">{matchInvSelected.size} inv</span></div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-muted-foreground">Money OUT (selected)</div>
              <div className="font-heading font-bold tabular-nums text-rose-700" data-testid="match-out-total">-₱{disTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-[10px] text-muted-foreground ml-1">{matchDisSelected.size} disb</span></div>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-2">
              <div className="text-muted-foreground text-xs uppercase tracking-widest">Net</div>
              <div className="font-heading font-black text-lg tabular-nums" data-testid="match-net-total">₱{(invTotal - disTotal).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setMatchOpen(false)} disabled={matchSaving}>Cancel</Button>
            <Button type="button" onClick={saveMatches} disabled={matchSaving || matchLoading} data-testid="match-save-btn">
              {matchSaving ? "Saving…" : "Save links"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
